import { Checkbox, FormControlLabel, Grid, TextField, Typography, MenuItem, InputAdornment, Switch } from '@mui/material'
import { get } from 'lodash'
import { useIntl } from 'react-intl'
import * as yup from 'yup'
import { styled } from '@mui/material/styles';

import { FormHandler } from 'components'
import { useCompany } from 'contexts'
import { useUser } from 'contexts'
import { SPARCK_COMPANY } from 'constants/app'
import { API } from 'core/requests'
import { t } from 'i18n'
import { CompanyType } from 'types'
import { getEnvironmentName } from 'core/environments'

const applications = [
  {
    id: 'beheard_application',
    label: '#BeHeard',
  },
  {
    id: 'beseen_application',
    label: '#BeSeen',
  },
  {
    id: 'pulse_application',
    label: '#Pulse',
  },
]

const validationSchema = yup.object({
  name: yup.string().required(t('companies.validations.companyNameIsRequired')),
  allow_internationalization: yup.boolean(),
})

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#1bb4ac',
        ...theme.applyStyles('dark', {
          backgroundColor: '#048982ff',
        }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
    ...theme.applyStyles('dark', {
      backgroundColor: 'rgba(255,255,255,.35)',
    }),
  },
}));

export default function ManageCompany({
  dialog,
  closeDialog,
  fetchRows,
}: any) {
  const intl = useIntl()
  const { company, setCompany } = useCompany()!
  const { user } = useUser()!
  const environment = getEnvironmentName()

  const initialValues: CompanyType = dialog.data
    ? dialog.data
    : {
        name: '',
        beseen_application: false,
        beheard_application: false,
        pulse_application: false,
        allow_internationalization: false,
        has_integrations: false,
        credit_card_fee_type: 'fixed', // fixed or percentage Assuming 'fixed' is the default fee type
        credit_card_fee: 0,
        ach_fee_type: 'fixed', // fixed or percentage Assuming 'fixed' is the default fee type
        ach_fee: 0,
        stripe_mode: false,
      }

  return (
    <FormHandler
      title={intl.formatMessage({ id: 'companies.title' })}
      requestUrl='companies'
      customMutationRequest={(payload: any) => {
        const companyId = get(payload, 'id') || get(company, 'id')

        return API.update('companies', payload, {
          pathParams: {
            id: companyId ? companyId : null,
          },
        }).then((response: any) => {
          // Keep the active company context + cache in sync so the budget page
          // picks the correct Stripe (test/live) publishable key after a
          // stripe_mode change, instead of serving the stale cached company.
          const updated = response?.company ?? response
          if (updated?.id && company?.id === updated.id) {
            setCompany(updated)
            localStorage.setItem(SPARCK_COMPANY, JSON.stringify(updated))
          }
          return response
        })
      }}
      data={dialog.data}
      closeDialog={closeDialog}
      fetchRows={fetchRows}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ formik }) => (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              id='name'
              name='name'
              data-cy='companyNameInput'
              label={intl.formatMessage({ id: 'companies.companyName' })}
              variant='outlined'
              value={formik.values.name}
              required
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            {applications.map(application => (
              <FormControlLabel
                key={application.id}
                control={
                  <Checkbox
                    checked={!!formik.values[application.id]}
                    onChange={formik.handleChange}
                    name={application.id}
                    data-cy='checkbox'
                  />
                }
                label={intl.formatMessage(
                  { id: 'companies.allowApplication' },
                  { application: application.label },
                )}
              />
            ))}

            <FormControlLabel
              key={'enable_rewards_store'}
              control={
                <Checkbox
                  checked={!!formik.values.enable_rewards_store}
                  onChange={formik.handleChange}
                  name={'enable_rewards_store'}
                  data-cy='checkbox'
                />
              }
              label={intl.formatMessage({
                id: 'companies.enableRewardsStore',
              })}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Typography variant='body1' fontWeight={700}>
                  {t('companies.budgetSettings.title')}
                </Typography>
              </Grid>

              {
                (environment === "development" && user?.sparck_administrator) && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      key={'stripe_mode'}
                      control={
                        <AntSwitch 
                          checked={!!formik.values.stripe_mode} 
                          inputProps={{ 'aria-label': 'ant design' }} 
                          sx={{ ml: 1, mr: 1 }}
                          onChange={() =>
                            formik.setFieldValue(
                              'stripe_mode',
                              !formik.values.stripe_mode,
                            )
                          }
                          name={`stripe_mode`}
                        />
                      }
                      label={
                        <Typography
                          variant="body1"
                          sx={{
                            color: formik.values.stripe_mode ? "success.main" : "error.main",
                            fontWeight: 500,
                          }}
                        >
                          {formik.values.stripe_mode ? 'Stripe Payments (Live Mode)' : 'Stripe Payments (Test Mode)'}
                        </Typography>
                      }
                    />

                    {formik.values.id &&
                      formik.values.stripe_mode !== formik.initialValues.stripe_mode && (
                        <Typography
                          variant='caption'
                          display='block'
                          sx={{ mt: 0.5, color: 'warning.main' }}
                        >
                          {`Saving will recreate this company's Stripe customer in ${formik.values.stripe_mode ? 'live' : 'test'} mode. The current Stripe customer — including its saved cards and bank accounts — will be discarded.`}
                        </Typography>
                      )}
                  </Grid>
                )
              }

              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12} sm={12} md={12}>
                    <Typography variant='body2' component='span' fontWeight={700}>
                      {t('companies.budgetSettings.creditCard.title')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} sx={{ mr: 2 }}>
                    <TextField
                      select
                      id='credit_card_fee_type'
                      name='credit_card_fee_type'
                      label={t('companies.budgetSettings.feeType.title')}
                      variant='outlined'
                      defaultValue={formik.values.credit_card_fee_type}
                      value={formik.values.credit_card_fee_type}
                      onChange={(event) =>
                        formik.setFieldValue(
                          'credit_card_fee_type',
                          event.target.value,
                        )
                      }
                      fullWidth
                      margin='dense'
                      size='small'
                    >
                      <MenuItem key={'fixed'} value={'fixed'}>
                        {'Fixed'}
                      </MenuItem>
                      <MenuItem key={'percentage'} value={'percentage'}>
                        {'percentage'}
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <TextField
                      id='credit_card_fee'
                      name='credit_card_fee'
                      label={t(
                        'companies.budgetSettings.creditCard.title',
                      )}
                      variant='outlined'
                      value={formik.values.credit_card_fee ?? 0}
                      onChange={(event) =>
                        formik.setFieldValue(
                          'credit_card_fee',
                          event.target.value,
                        )
                      }
                      fullWidth
                      margin='dense'
                      size='small'
                      // InputProps={{
                      //   inputComponent: formik.values.credit_card_fee_type === 'fixed' ? MoneyInput : 'input',
                      // }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {formik.values.credit_card_fee_type === 'fixed' ? '$' : '%'}
                          </InputAdornment>
                        ),
                        inputProps: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          type: 'number',
                          min: 0 // Ensures no negative values
                        }
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12} sm={12} md={12}>
                    <Typography variant='body2' component='span' fontWeight={700}>
                      {t('companies.budgetSettings.ach.title')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} sx={{ mr: 2 }}>
                    <TextField
                      select
                      id='ach_fee_type'
                      name='ach_fee_type'
                      label={t('companies.budgetSettings.feeType.title')}
                      variant='outlined'
                      defaultValue={formik.values.ach_fee_type}
                      value={formik.values.ach_fee_type}
                      onChange={(event) =>
                        formik.setFieldValue(
                          'ach_fee_type',
                          event.target.value,
                        )
                      }
                      fullWidth
                      margin='dense'
                      size='small'
                    >
                      <MenuItem key={'fixed'} value={'fixed'}>
                        {'Fixed'}
                      </MenuItem>
                      <MenuItem key={'percentage'} value={'percentage'}>
                        {'percentage'}
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <TextField
                      id='ach_fee'
                      name='ach_fee'
                      label={t(
                        'companies.budgetSettings.ach.title',
                      )}
                      variant='outlined'
                      value={formik.values.ach_fee ?? 0}
                      onChange={(event) =>
                        formik.setFieldValue(
                          'ach_fee',
                          event.target.value,
                        )
                      }
                      fullWidth
                      margin='dense'
                      size='small'
                      // InputProps={{
                      //   inputComponent: formik.values.ach_fee_type === 'fixed' ? MoneyInput : 'input',
                      // }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {formik.values.ach_fee_type === 'fixed' ? '$' : '%'}
                          </InputAdornment>
                        ),
                        inputProps: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          type: 'number',
                          min: 0 // Ensures no negative values
                        }
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              key={'allow_internationalization'}
              control={
                <Checkbox
                  checked={!!formik.values.allow_internationalization}
                  onChange={formik.handleChange}
                  name={'allow_internationalization'}
                  data-cy='checkbox'
                />
              }
              label={intl.formatMessage({
                id: 'companies.allowInternationalization',
              })}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              key={'has_integrations'}
              control={
                <Checkbox
                  checked={!!formik.values.has_integrations}
                  onChange={() =>
                    formik.setFieldValue(
                      'has_integrations',
                      !formik.values.has_integrations,
                    )
                  }
                  name={'has_integrations'}
                  data-cy='checkbox'
                />
              }
              label={'Has external Sparck API access'}
            />
          </Grid>

        </Grid>
      )}
    </FormHandler>
  )
}
